import { Form, FormField, FormItem } from '@/components/ui/form';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import Title from '@/components/ui/Title';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Check, Plus, Trash2, X } from 'lucide-react';
import Loader from '@/components/ui/Loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import type { QuizQuestion, QuizTemplate } from '@/features/webinar/types';
import ColorSelector from '@/components/ui/ColorSelector';
import { cn } from '@/lib/utils';
import { getReadableTextColor } from '@/utils/colors';
import type { InteractionItemHandle } from '../interactionBatch';

const quizQuestionTypeSchema = z.enum(['SINGLE_CHOICE', 'MULTIPLE_CHOICE']);

function createQuizSchemas(t: (key: string) => string) {
  const answerSchema = z.object({
    id: z.number().optional(),
    position: z.number().int().nonnegative().optional().nullable(),
    textContent: z
      .string()
      .min(1, t('quizForm.validation.answerEmpty'))
      .max(300, t('quizForm.validation.answerMaxLength'))
      .refine((val) => val.trim().length > 0, {
        message: t('quizForm.validation.answerEmpty'),
      }),
    correct: z.boolean().default(false),
  });

  const questionSchema = z
    .object({
      id: z.number().optional(),
      textContent: z
        .string()
        .min(1, t('quizForm.validation.questionEmpty'))
        .max(300, t('quizForm.validation.questionMaxLength'))
        .refine((val) => val.trim().length > 0, {
          message: t('quizForm.validation.questionEmpty'),
        }),
      type: quizQuestionTypeSchema.default('SINGLE_CHOICE'),
      position: z.number().int().nonnegative().optional().nullable(),
      points: z.number().int().nonnegative().optional().nullable(),
      timeLimitSeconds: z.number().int().positive().optional().nullable(),
      options: z
        .array(answerSchema)
        .min(2, t('quizForm.validation.minAnswers'))
        .max(6, t('quizForm.validation.maxAnswers')),
    })
    .superRefine(({ type, options }, ctx) => {
      const correctAnswersCount = options.filter((answer) => answer.correct).length;
      if (type === 'SINGLE_CHOICE' && correctAnswersCount !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('quizForm.validation.singleChoiceCorrect'),
          path: ['options'],
        });
      }
      if (type === 'MULTIPLE_CHOICE' && correctAnswersCount < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('quizForm.validation.multipleChoiceCorrect'),
          path: ['options'],
        });
      }
    });

  const quizSchema = z.object({
    name: z
      .string()
      .min(1, t('quizForm.validation.quizTitleRequired'))
      .max(100, t('quizForm.validation.quizTitleMaxLength'))
      .refine((val) => val.trim().length > 0, {
        message: t('quizForm.validation.quizTitleEmpty'),
      }),
    enabledDuringLive: z.boolean(),
    backgroundColor: z.string().optional(),
    borderColor: z.string().optional(),
    titleColor: z.string().optional(),
    accentColor: z.string().optional(),
    customBackgroundColor: z.string().optional(),
    customBorderColor: z.string().optional(),
    customAccentColor: z.string().optional(),
    question: questionSchema,
  });

  return { quizSchema };
}

type QuizFormValues = z.input<ReturnType<typeof createQuizSchemas>['quizSchema']>;

interface QuizFormProps {
  quizIndex: number;
  quizId: string;
  onRemove: () => void;
  canRemove: boolean;
  onSubmit: (data: QuizFormValues) => void;
  onRemoveAnswerOption?: (quizId: string, answerOptionId: number) => void;
  existingQuiz?: QuizTemplate;
  isModal?: boolean;
  onSave?: () => void;
  onDelete?: () => void;
  isSubmitting?: boolean;
  isEvergreen?: boolean;
  readOnly?: boolean;
  // Mode sauvegarde globale : masque le bouton « Enregistrer » par élément.
  batchMode?: boolean;
  // Remonte l'état « modifications non enregistrées » au groupe parent.
  onDirtyChange?: (dirty: boolean) => void;
}

const QuizForm = forwardRef<InteractionItemHandle, QuizFormProps>(function QuizForm(
  {
    quizIndex,
    quizId,
    onRemove,
    canRemove,
    onSubmit,
    onRemoveAnswerOption,
    existingQuiz,
    isModal = false,
    onSave,
    onDelete,
    isSubmitting = false,
    isEvergreen = false,
    readOnly,
    batchMode = false,
    onDirtyChange,
  },
  ref,
) {
  const { t } = useTranslation('createWebinar');
  const locked = readOnly === true;
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );
  const [selectedRadioIndex, setSelectedRadioIndex] = useState<number>(0);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<Set<number>>(new Set([0]));

  const { quizSchema } = useMemo(() => createQuizSchemas(t), [t]);

  const isCustomColor = (color: string): boolean => {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  };

  const mapStoredColorToPresetId = (color: string | null | undefined): string | undefined => {
    if (!color) return undefined;

    // Si la valeur est "gradient1" ou "gradient2", retourner gradient1-color1 ou gradient2-color1
    if (color === 'gradient1') return 'gradient1-color1';
    if (color === 'gradient2') return 'gradient2-color1';

    // Couleurs venant de la charte graphique du workspace
    if (color === visualIdentity?.accentColor) return 'accent';
    if (color === visualIdentity?.backgroundColor) return 'background';
    if (color === visualIdentity?.contrastColor) return 'contrast';
    if (color === visualIdentity?.secondaryBackgroundColor) return 'secondary-background';
    if (color === visualIdentity?.gradient1Color1) return 'gradient1-color1';
    if (color === visualIdentity?.gradient2Color1) return 'gradient2-color1';

    // Couleurs "fixes"
    if (color === '#000000') return 'black';
    if (color === '#FFFFFF') return 'white';

    return undefined;
  };

  const buildOptions = (
    source?: QuizFormValues['question']['options'],
    fallback?: QuizQuestion['options'],
  ): QuizFormValues['question']['options'] => {
    if (source && source.length > 0) {
      return source.map((option, index) => ({
        id: option.id,
        textContent: option.textContent ?? '',
        correct: option.correct ?? false,
        position: option.position ?? index,
      }));
    }

    if (fallback && fallback.length > 0) {
      return fallback.map((option, index) => ({
        id: option.id,
        textContent: option.textContent,
        correct: option.correct,
        position: option.position ?? index,
      }));
    }

    return [
      { textContent: '', correct: true, position: 0 },
      { textContent: '', correct: false, position: 1 },
    ];
  };

  const defaultQuestionValues: QuizFormValues['question'] = {
    textContent: existingQuiz?.questions?.[0]?.textContent ?? '',
    type: existingQuiz?.questions?.[0]?.type ?? 'SINGLE_CHOICE',
    position: existingQuiz?.questions?.[0]?.position ?? null,
    points: existingQuiz?.questions?.[0]?.points ?? null,
    timeLimitSeconds: existingQuiz?.questions?.[0]?.timeLimitSeconds ?? null,
    options: buildOptions(
      existingQuiz?.questions?.[0]?.options,
      existingQuiz?.questions?.[0]?.options,
    ),
  };

  const resolvedName = existingQuiz?.name ?? '';
  const resolvedDisableChat = existingQuiz?.enabledDuringLive ?? false;

  const resolvedBackgroundColor = existingQuiz?.backgroundColor ?? '';
  const mappedBackgroundColor = mapStoredColorToPresetId(resolvedBackgroundColor);
  const defaultBackgroundColor = isCustomColor(resolvedBackgroundColor)
    ? 'custom'
    : mappedBackgroundColor || resolvedBackgroundColor || 'accent';
  const resolvedCustomBackgroundColor = isCustomColor(resolvedBackgroundColor)
    ? resolvedBackgroundColor
    : (existingQuiz?.backgroundColor ?? '#000000');

  const resolvedBorderColor = existingQuiz?.borderColor ?? '';
  const mappedBorderColor = mapStoredColorToPresetId(resolvedBorderColor);
  const defaultBorderColor = isCustomColor(resolvedBorderColor)
    ? 'custom'
    : mappedBorderColor || resolvedBorderColor || 'no-border';
  const resolvedCustomBorderColor = isCustomColor(resolvedBorderColor)
    ? resolvedBorderColor
    : (existingQuiz?.borderColor ?? '#000000');

  const resolvedAccentColor = existingQuiz?.accentColor ?? '';
  const mappedAccentColor = mapStoredColorToPresetId(resolvedAccentColor);
  const defaultAccentColor = isCustomColor(resolvedAccentColor)
    ? 'custom'
    : mappedAccentColor || resolvedAccentColor || 'contrast';
  const resolvedCustomAccentColor = isCustomColor(resolvedAccentColor)
    ? resolvedAccentColor
    : (existingQuiz?.accentColor ?? '#000000');

  const resolvedTitleColor = existingQuiz?.titleColor ?? '#000000';

  const form = useForm<QuizFormValues>({
    resolver: zodResolver(quizSchema),
    defaultValues: {
      name: resolvedName,
      enabledDuringLive: resolvedDisableChat,
      backgroundColor: defaultBackgroundColor,
      borderColor: defaultBorderColor,
      titleColor: resolvedTitleColor,
      accentColor: defaultAccentColor,
      customBackgroundColor: resolvedCustomBackgroundColor,
      customBorderColor: resolvedCustomBorderColor,
      customAccentColor: resolvedCustomAccentColor,
      question: defaultQuestionValues,
    },
    mode: 'onChange',
  });

  const isDirty = form.formState.isDirty;

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => form.formState.isDirty,
      validate: () => form.trigger(),
      getValues: () => form.getValues(),
      markPristine: () => form.reset(form.getValues()),
    }),
    [form],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const addAnswer = () => {
    if (locked) return;
    const currentOptions = form.getValues('question.options');
    if (currentOptions.length < 6) {
      const nextPosition = currentOptions.length;
      form.setValue('question.options', [
        ...currentOptions,
        { textContent: '', correct: false, position: nextPosition },
      ]);
    }
  };

  const removeAnswer = async (answerIndex: number) => {
    if (locked) return;
    const currentOptions = form.getValues('question.options');
    if (currentOptions.length <= 2) {
      return;
    }

    const option = currentOptions[answerIndex];
    if (option?.id && onRemoveAnswerOption) {
      try {
        onRemoveAnswerOption(quizId, option.id);
      } catch (error) {
        console.error('Failed to delete quiz answer option', error);
        return;
      }
    }

    const newAnswers = currentOptions.filter((_, index) => index !== answerIndex);
    form.setValue('question.options', newAnswers);
  };

  const toggleCorrectAnswer = (answerIndex: number) => {
    const currentAnswers = form.getValues('question.options');
    const questionType = form.getValues('question.type');

    const correctAnswersCount = currentAnswers.filter((answer) => answer.correct).length;

    if (questionType === 'SINGLE_CHOICE') {
      currentAnswers.forEach((_, index) => {
        form.setValue(`question.options.${index}.correct`, index === answerIndex);
      });
      return;
    }

    if (currentAnswers[answerIndex].correct && correctAnswersCount > 1) {
      form.setValue(`question.options.${answerIndex}.correct`, false);
    } else if (!currentAnswers[answerIndex].correct) {
      form.setValue(`question.options.${answerIndex}.correct`, true);
    }
  };

  const handleSubmit = async (data: QuizFormValues) => {
    if (locked) return;
    // Trim all text fields before submitting
    const trimmedData: QuizFormValues = {
      ...data,
      name: data.name.trim(),
      question: {
        ...data.question,
        textContent: data.question.textContent.trim(),
        options: data.question.options.map((option) => ({
          ...option,
          textContent: option.textContent.trim(),
        })),
      },
    };
    await onSubmit(trimmedData);
  };

  useEffect(() => {
    if (onSave) {
      const saveFunction = () => {
        form.handleSubmit(handleSubmit)();
      };

      (window as any).quizFormSave = saveFunction;
    }

    if (onDelete) {
      (window as any).quizFormDelete = onDelete;
    }

    return () => {
      delete (window as any).quizFormSave;
      delete (window as any).quizFormDelete;
    };
  }, [onSave, onDelete, form]);

  useEffect(() => {
    const options = form.watch('question.options');
    if (options && options.length > 0) {
      if (form.watch('question.type') === 'SINGLE_CHOICE') {
        setSelectedRadioIndex(0);
      } else {
        setSelectedCheckboxes(new Set([0]));
      }
    }
  }, [form.watch('question.options'), form.watch('question.type')]);

  const colorsArray = [
    {
      id: 'accent',
      name: t('quizForm.colorNames.accent'),
      value: visualIdentity?.accentColor,
      color: visualIdentity?.accentColor,
    },
    {
      id: 'background',
      name: t('quizForm.colorNames.background'),
      value: visualIdentity?.backgroundColor,
      color: visualIdentity?.backgroundColor,
    },
    {
      id: 'contrast',
      name: t('quizForm.colorNames.contrast'),
      value: visualIdentity?.contrastColor,
      color: visualIdentity?.contrastColor,
    },
    {
      id: 'gradient1-color1',
      name: t('quizForm.colorNames.gradient1'),
      value: visualIdentity?.gradient1Color1,
      color: visualIdentity?.gradient1Color1,
    },
    {
      id: 'gradient2-color1',
      name: t('quizForm.colorNames.gradient2'),
      value: visualIdentity?.gradient2Color1,
      color: visualIdentity?.gradient2Color1,
    },
    {
      id: 'secondary-background',
      name: t('quizForm.colorNames.secondaryBackground'),
      value: visualIdentity?.secondaryBackgroundColor,
      color: visualIdentity?.secondaryBackgroundColor,
    },
    { id: 'black', name: t('quizForm.colorNames.black'), value: '#000000', color: '#000000' },
    { id: 'white', name: t('quizForm.colorNames.white'), value: '#FFFFFF', color: '#FFFFFF' },
  ];

  const arrayFontColors = [
    { name: t('quizForm.colorNames.black'), value: '#000000' },
    { name: t('quizForm.colorNames.white'), value: '#FFFFFF' },
  ];

  const getColorValueById = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return 'transparent';
    }
    if (colorId === 'custom') {
      return customColor || '#FFFFFF';
    }
    const color = colorsArray.find((c) => c.id === colorId);
    return color?.value || '#FFFFFF';
  };

  const getBackgroundStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'gradient1-color1' || colorId === 'gradient1-color2') {
      return {
        background: `linear-gradient(to bottom, ${visualIdentity?.gradient1Color1}, ${visualIdentity?.gradient1Color2})`,
      };
    }
    if (colorId === 'gradient2-color1' || colorId === 'gradient2-color2') {
      return {
        background: `linear-gradient(226deg, ${visualIdentity?.gradient2Color1} 0.2%, ${visualIdentity?.gradient2Color2} 104.63%)`,
      };
    }
    return {
      backgroundColor: getColorValueById(colorId, customColor),
    };
  };

  const getBorderStyle = (colorId: string, customColor?: string) => {
    if (colorId === 'no-border') {
      return {
        border: 'none',
      };
    }
    return {
      borderColor: getColorValueById(colorId, customColor),
    };
  };

  const getFinalColor = (colorType: 'backgroundColor' | 'borderColor' | 'accentColor') => {
    const colorId = form.watch(colorType);
    const customColor = form.watch(
      `custom${colorType.charAt(0).toUpperCase() + colorType.slice(1)}` as any,
    );

    if (colorId === 'custom') {
      return customColor || '#000000';
    }

    return getColorValueById(colorId || 'accent', customColor);
  };

  return (
    <Form {...form}>
      {isModal ? (
        <div className={cn('flex flex-col gap-4', locked && 'pointer-events-none opacity-90')}>
          <div className="flex flex-col w-full">
            <div className="flex items-center gap-2 w-full">
              {/* <div className="flex-1 ">
                <FormField
                  name="name"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <InputLabel
                        label={t('quizForm.privateTitle', { index: quizIndex + 1 })}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              </div> */}
              {canRemove && !locked && (
                <button
                  onClick={onRemove}
                  className="bg-white rounded-lg mr-4 py-3 px-2 border border-input cursor-pointer"
                >
                  <Trash2 />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <Title level={2}>{t('quizForm.title')}</Title>

            <div className="flex items-center space-x-2">
              <FormField
                name="enabledDuringLive"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between w-full">
                    <Label htmlFor="enabledDuringLive">{t('quizForm.disableChat')}</Label>
                    <Switch
                      id="enabledDuringLive"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormItem>
                )}
              />
            </div>

            {/* Section Question */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <FormField
                      name="question.textContent"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <TextFieldWithLimit
                            label={t('quizForm.questionLabel')}
                            value={field.value ?? ''}
                            onChange={(v) => field.onChange(v)}
                            maxLength={300}
                            error={fieldState.error?.message}
                          />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Section Réponses */}
                <div className="flex flex-col gap-2 pt-4">
                  <div className="space-y-4">
                    {form.watch('question.options')?.map((_, answerIndex) => (
                      <div key={answerIndex} className="flex flex-col">
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <FormField
                              name={`question.options.${answerIndex}.textContent`}
                              control={form.control}
                              render={({ field, fieldState }) => (
                                <FormItem>
                                  <TextFieldWithLimit
                                    label={t('quizForm.answerLabel', { index: answerIndex + 1 })}
                                    value={field.value ?? ''}
                                    onChange={(v) => field.onChange(v)}
                                    maxLength={300}
                                    error={fieldState.error?.message}
                                  />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Bouton pour marquer comme correcte */}
                          <button
                            type="button"
                            onClick={() => toggleCorrectAnswer(answerIndex)}
                            className={`flex items-center justify-center w-10 h-14 rounded-lg border-2 transition-colors cursor-pointer ${
                              form.watch(`question.options.${answerIndex}.correct`)
                                ? 'bg-green-100 border-input text-green-600'
                                : 'bg-red-100 border-input text-red-600 hover:bg-red-200'
                            }`}
                          >
                            {form.watch(`question.options.${answerIndex}.correct`) ? (
                              <Check className="size-6 text-green-500" />
                            ) : (
                              <X className="size-6 text-red-500" />
                            )}
                          </button>

                          {form.watch('question.options')?.length > 2 && !locked && (
                            <button
                              type="button"
                              onClick={() => removeAnswer(answerIndex)}
                              className="bg-white rounded-lg h-14 py-3 px-2 border border-input cursor-pointer"
                            >
                              <Trash2 className="size-6" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {form.watch('question.options')?.length < 6 && !locked && (
                    <button
                      type="button"
                      onClick={() => addAnswer()}
                      className="flex items-center gap-2 pt-4"
                    >
                      <Plus className="size-6" />
                      <Label className="cursor-pointer text-sm">{t('quizForm.addAnswer')}</Label>
                    </button>
                  )}

                  {form.formState.errors.question?.options && (
                    <div className="text-left text-red-500 font-medium ml-0">
                      {form.formState.errors.question.options.root?.message ||
                        form.formState.errors.question.options.message}
                    </div>
                  )}
                </div>
              </div>

              <Separator className="bg-input" />

              <div className="flex flex-col gap-4 pt-8">
                <Title level={2}>{t('quizForm.colors')}</Title>

                <div className="grid grid-cols-2 gap-4">
                  {/* background */}
                  <ColorSelector
                    form={form}
                    offerType="A"
                    colorsArray={colorsArray}
                    colorFieldName="backgroundColor"
                    customColorFieldName="customBackgroundColor"
                    label={t('quizForm.backgroundColor')}
                    customColorLabel={t('quizForm.customColor')}
                    getFinalColor={(colorType) => getFinalColor(colorType as 'backgroundColor')}
                    getStyle={getBackgroundStyle}
                  />

                  {/* Borders */}
                  <ColorSelector
                    form={form}
                    offerType="A"
                    colorsArray={colorsArray}
                    colorFieldName="borderColor"
                    customColorFieldName="customBorderColor"
                    label={t('quizForm.borderColor')}
                    customColorLabel={t('quizForm.customColor')}
                    noBorderLabel={t('quizForm.noBorder')}
                    showNoBorder={true}
                    getFinalColor={(colorType) => getFinalColor(colorType as 'borderColor')}
                    getStyle={(colorId, customColor) => {
                      if (colorId === 'no-border') {
                        return { backgroundColor: 'white' };
                      }
                      return getBackgroundStyle(colorId, customColor);
                    }}
                  />

                  {/* Title */}
                  <div className="flex items-center gap-4">
                    <div
                      className="size-14 flex-shrink-0 rounded-lg border border-input"
                      style={{
                        backgroundColor: form.watch('titleColor') || '#000000',
                      }}
                    />

                    <div className="w-full">
                      <FormField
                        name="titleColor"
                        control={form.control}
                        render={({ field }) => (
                          <FormItem className="h-14">
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger label={t('quizForm.titleColor')} className="h-14">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {arrayFontColors.map((color) => (
                                  <SelectItem key={color.name} value={color.value}>
                                    {color.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Accent */}
                  <ColorSelector
                    form={form}
                    offerType="A"
                    colorsArray={colorsArray}
                    colorFieldName="accentColor"
                    customColorFieldName="customAccentColor"
                    label={t('quizForm.accentColor')}
                    customColorLabel={t('quizForm.customColor')}
                    getFinalColor={(colorType) => getFinalColor(colorType as 'accentColor')}
                    getStyle={getBackgroundStyle}
                  />
                </div>
              </div>

              <Accordion type="single" collapsible className="pt-8" defaultValue="preview">
                <AccordionItem value="preview">
                  <AccordionTrigger isChevron className=" rounded-[20px] p-0 space-y-10">
                    <Title level={2}>{t('quizForm.preview')}</Title>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="w-full  flex flex-col gap-4 ">
                      <div
                        className="flex flex-col w-full gap-4 rounded-lg border-2 p-4 max-w-[607px] mx-auto "
                        style={{
                          ...getBackgroundStyle(
                            form.watch('backgroundColor') || 'accent',
                            form.watch('customBackgroundColor'),
                          ),
                          ...getBorderStyle(
                            form.watch('borderColor') || 'accent',
                            form.watch('customBorderColor'),
                          ),
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <h3
                            className="text-lg font-semibold"
                            style={{
                              color: form.watch('titleColor') || '#000000',
                            }}
                          >
                            {form.watch('question.textContent') || t('quizForm.questionLabel')}
                          </h3>
                        </div>

                        {form.watch('question.type') === 'SINGLE_CHOICE' ? (
                          <RadioGroup
                            value={String(selectedRadioIndex)}
                            onValueChange={(value) => setSelectedRadioIndex(Number(value))}
                          >
                            {form.watch('question.options')?.map((answer, answerIndex) => (
                              <div
                                key={answerIndex}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                              >
                                <RadioGroupItem
                                  value={String(answerIndex)}
                                  id={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  style={
                                    {
                                      '--accent-color': getFinalColor('accentColor'),
                                    } as React.CSSProperties
                                  }
                                  className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                                />
                                <Label
                                  htmlFor={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {answer.textContent ||
                                    t('quizForm.answerLabel', { index: answerIndex + 1 })}
                                </Label>
                              </div>
                            ))}
                            <Button
                              className="w-full"
                              style={{
                                backgroundColor: getFinalColor('accentColor'),
                                color: getReadableTextColor(getFinalColor('accentColor')),
                              }}
                            >
                              {isSubmitting ? t('quizForm.submitting') : t('quizForm.submit')}
                            </Button>
                          </RadioGroup>
                        ) : (
                          <div className="flex flex-col space-y-2">
                            {form.watch('question.options')?.map((answer, answerIndex) => (
                              <div
                                key={answerIndex}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                              >
                                <Checkbox
                                  id={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  checked={selectedCheckboxes.has(answerIndex)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedCheckboxes);
                                    if (checked) {
                                      newSet.add(answerIndex);
                                    } else {
                                      newSet.delete(answerIndex);
                                    }
                                    setSelectedCheckboxes(newSet);
                                  }}
                                  style={
                                    {
                                      '--accent-color': getFinalColor('accentColor'),
                                    } as React.CSSProperties
                                  }
                                  className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                                />
                                <Label
                                  htmlFor={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {answer.textContent ||
                                    t('quizForm.answerLabel', { index: answerIndex + 1 })}
                                </Label>
                              </div>
                            ))}
                            <Button
                              className="w-full"
                              style={{
                                backgroundColor: getFinalColor('accentColor'),
                                color: getReadableTextColor(getFinalColor('accentColor')),
                              }}
                            >
                              {isSubmitting ? t('quizForm.submitting') : t('quizForm.submit')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <AccordionItem
            value={`item-${quizIndex}`}
            className={`bg-medium p-6 ${isEvergreen ? 'rounded-none rounded-t-[20px] border-b-2' : ''}`}
          >
            <div className="flex flex-col w-full">
              <div className="flex items-start gap-2 w-full">
                <div className={cn('flex-1', locked && 'pointer-events-none opacity-90')}>
                  <FormField
                    name="name"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <TextFieldWithLimit
                          className="bg-white"
                          label={t('quizForm.privateTitle', { index: quizIndex + 1 })}
                          value={field.value ?? ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={100}
                          error={fieldState.error?.message}
                        />
                      </FormItem>
                    )}
                  />
                </div>
                {canRemove && !locked && (
                  <button
                    onClick={onRemove}
                    className="bg-white rounded-lg mr-4  px-2 h-14 border border-input cursor-pointer"
                  >
                    <Trash2 className="" />
                  </button>
                )}
                <AccordionTrigger isChevron className=" rounded-[20px] p-0 h-14" />
              </div>
            </div>
            <AccordionContent className="flex flex-col gap-4 bg-medium pt-4 pb-0 px-0">
              <div
                className={cn('flex flex-col gap-4', locked && 'pointer-events-none opacity-90')}
              >
                <Title level={2}>{t('quizForm.title')}</Title>

                <div className="flex items-center space-x-2">
                  <FormField
                    name="enabledDuringLive"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between w-full">
                        <Label htmlFor="enabledDuringLive">{t('quizForm.disableChat')}</Label>
                        <Switch
                          id="enabledDuringLive"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Section Question */}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <FormField
                          name="question.textContent"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <FormItem>
                              <TextFieldWithLimit
                                className="bg-white"
                                label={t('quizForm.questionLabel')}
                                value={field.value ?? ''}
                                onChange={(v) => field.onChange(v)}
                                maxLength={300}
                                error={fieldState.error?.message}
                              />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Section Réponses */}
                    <div className="flex flex-col gap-2 pt-4">
                      <div className="space-y-4">
                        {form.watch('question.options')?.map((_, answerIndex) => (
                          <div key={answerIndex} className="flex flex-col">
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <FormField
                                  name={`question.options.${answerIndex}.textContent`}
                                  control={form.control}
                                  render={({ field, fieldState }) => (
                                    <FormItem>
                                      <TextFieldWithLimit
                                        className="bg-white"
                                        label={t('quizForm.answerLabel', {
                                          index: answerIndex + 1,
                                        })}
                                        value={field.value ?? ''}
                                        onChange={(v) => field.onChange(v)}
                                        maxLength={300}
                                        error={fieldState.error?.message}
                                      />
                                    </FormItem>
                                  )}
                                />
                              </div>

                              {/* Bouton pour marquer comme correcte */}
                              <button
                                type="button"
                                onClick={() => toggleCorrectAnswer(answerIndex)}
                                className={`flex items-center justify-center w-10 h-14 rounded-lg border-2 transition-colors cursor-pointer ${
                                  form.watch(`question.options.${answerIndex}.correct`)
                                    ? 'bg-green-100 border-input text-green-600'
                                    : 'bg-red-100 border-input text-red-600 hover:bg-red-200'
                                }`}
                              >
                                {form.watch(`question.options.${answerIndex}.correct`) ? (
                                  <Check className="size-6 text-green-500" />
                                ) : (
                                  <X className="size-6 text-red-500" />
                                )}
                              </button>

                              {form.watch('question.options')?.length > 2 && !locked && (
                                <button
                                  type="button"
                                  onClick={() => removeAnswer(answerIndex)}
                                  className="bg-white rounded-lg h-14 py-3 px-2 border border-input cursor-pointer"
                                >
                                  <Trash2 className="size-6" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {form.watch('question.options')?.length < 6 && !locked && (
                        <button
                          type="button"
                          onClick={() => addAnswer()}
                          className="flex items-center gap-2 pt-4"
                        >
                          <Plus className="size-6" />
                          <Label className="cursor-pointer text-sm ">
                            {t('quizForm.addAnswer')}
                          </Label>
                        </button>
                      )}

                      {form.formState.errors.question?.options && (
                        <div className="text-left text-red-500 font-medium ml-0">
                          {form.formState.errors.question.options?.root?.message ||
                            form.formState.errors.question.options?.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <Separator className="bg-input" />

                  <div className="flex flex-col gap-4 pt-8">
                    <Title level={2}>{t('quizForm.colors')}</Title>

                    <div className="grid lg:grid-cols-2 gap-4">
                      {/* background */}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={colorsArray}
                        colorFieldName="backgroundColor"
                        customColorFieldName="customBackgroundColor"
                        label={t('quizForm.backgroundColor')}
                        customColorLabel={t('quizForm.customColor')}
                        getFinalColor={(colorType) => getFinalColor(colorType as 'backgroundColor')}
                        getStyle={getBackgroundStyle}
                      />

                      {/* Borders */}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={colorsArray}
                        colorFieldName="borderColor"
                        customColorFieldName="customBorderColor"
                        label={t('quizForm.borderColor')}
                        customColorLabel={t('quizForm.customColor')}
                        noBorderLabel={t('quizForm.noBorder')}
                        showNoBorder={true}
                        getFinalColor={(colorType) => getFinalColor(colorType as 'borderColor')}
                        getStyle={(colorId, customColor) => {
                          if (colorId === 'no-border') {
                            return { backgroundColor: 'white' };
                          }
                          return getBackgroundStyle(colorId, customColor);
                        }}
                      />

                      {/* Title */}
                      <div className="flex items-center gap-4">
                        <div
                          className="size-14 flex-shrink-0 rounded-lg border border-input"
                          style={{
                            backgroundColor: form.watch('titleColor') || '#000000',
                          }}
                        />

                        <div className="w-full">
                          <FormField
                            name="titleColor"
                            control={form.control}
                            render={({ field }) => (
                              <FormItem className="h-14">
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <SelectTrigger label={t('quizForm.titleColor')} className="h-14">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {arrayFontColors.map((color) => (
                                      <SelectItem key={color.name} value={color.value}>
                                        {color.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {/* Accent */}
                      <ColorSelector
                        form={form}
                        offerType="A"
                        colorsArray={colorsArray}
                        colorFieldName="accentColor"
                        customColorFieldName="customAccentColor"
                        label={t('quizForm.accentColor')}
                        customColorLabel={t('quizForm.customColor')}
                        getFinalColor={(colorType) => getFinalColor(colorType as 'accentColor')}
                        getStyle={getBackgroundStyle}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-4 pt-8">
                    <Title level={2}>{t('quizForm.preview')}</Title>

                    <div className="w-full  flex flex-col gap-4 ">
                      <div
                        className="flex flex-col w-full gap-4 rounded-lg border-2 p-4 max-w-[607px] mx-auto "
                        style={{
                          ...getBackgroundStyle(
                            form.watch('backgroundColor') || 'accent',
                            form.watch('customBackgroundColor'),
                          ),
                          ...getBorderStyle(
                            form.watch('borderColor') || 'accent',
                            form.watch('customBorderColor'),
                          ),
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <h3
                            className="text-lg font-semibold"
                            style={{
                              color: form.watch('titleColor') || '#000000',
                            }}
                          >
                            {form.watch('question.textContent') || t('quizForm.questionLabel')}
                          </h3>
                        </div>

                        {form.watch('question.type') === 'SINGLE_CHOICE' ? (
                          <RadioGroup
                            value={String(selectedRadioIndex)}
                            onValueChange={(value) => setSelectedRadioIndex(Number(value))}
                          >
                            {form.watch('question.options')?.map((answer, answerIndex) => (
                              <div
                                key={answerIndex}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                              >
                                <RadioGroupItem
                                  value={String(answerIndex)}
                                  id={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  style={
                                    {
                                      '--accent-color': getFinalColor('accentColor'),
                                    } as React.CSSProperties
                                  }
                                  className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                                />
                                <Label
                                  htmlFor={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {answer.textContent ||
                                    t('quizForm.answerLabel', { index: answerIndex + 1 })}
                                </Label>
                              </div>
                            ))}
                            <Button
                              className="w-full"
                              style={{
                                backgroundColor: getFinalColor('accentColor'),
                                color: getReadableTextColor(getFinalColor('accentColor')),
                              }}
                            >
                              {isSubmitting ? t('quizForm.submitting') : t('quizForm.submit')}
                            </Button>
                          </RadioGroup>
                        ) : (
                          <div className="flex flex-col space-y-2">
                            {form.watch('question.options')?.map((answer, answerIndex) => (
                              <div
                                key={answerIndex}
                                className="flex items-center space-x-2 p-2 rounded-lg bg-white border border-input"
                              >
                                <Checkbox
                                  id={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  checked={selectedCheckboxes.has(answerIndex)}
                                  onCheckedChange={(checked) => {
                                    const newSet = new Set(selectedCheckboxes);
                                    if (checked) {
                                      newSet.add(answerIndex);
                                    } else {
                                      newSet.delete(answerIndex);
                                    }
                                    setSelectedCheckboxes(newSet);
                                  }}
                                  style={
                                    {
                                      '--accent-color': getFinalColor('accentColor'),
                                    } as React.CSSProperties
                                  }
                                  className="data-[state=checked]:bg-(--accent-color) data-[state=checked]:border-(--accent-color)"
                                />
                                <Label
                                  htmlFor={`quiz-${quizIndex}-answer-${answerIndex}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {answer.textContent ||
                                    t('quizForm.answerLabel', { index: answerIndex + 1 })}
                                </Label>
                              </div>
                            ))}
                            <Button
                              className="w-full"
                              style={{
                                backgroundColor: getFinalColor('accentColor'),
                                color: getReadableTextColor(getFinalColor('accentColor')),
                              }}
                            >
                              {isSubmitting ? t('quizForm.submitting') : t('quizForm.submit')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {!batchMode && (
                  <Button
                    type="submit"
                    onClick={form.handleSubmit(handleSubmit)}
                    className="mt-4 disabled:bg-gray-200 disabled:text-gray-500"
                    disabled={locked || isSubmitting || !form.formState.isValid}
                  >
                    {isSubmitting && <Loader />}
                    {t('quizForm.save')}
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </div>
      )}
    </Form>
  );
});

export default QuizForm;
